import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, UserCog } from "lucide-react";

const EMPTY = { name: "", email: "", password: "" };

export default function CaAccess() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/ca-accounts").then((r) => setAccounts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error("Name, email, and password are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/ca-accounts", form);
      toast.success("CA account created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteAccount = async (id) => {
    try {
      await api.delete(`/ca-accounts/${id}`);
      toast.success("CA account removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="CA Access">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#4B5563] max-w-xl">
          Create login credentials for your Chartered Accountant. CA accounts get read-only access to invoices, expenses, and the financial ledger, plus the ability to add reconciliation notes.
        </p>
        <Button size="sm" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-ca-account-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> New CA Login
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="ca-accounts-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#4B5563]">No CA accounts yet.</td></tr>}
            {accounts.map((a) => (
              <tr key={a.id} data-testid={`ca-account-row-${a.id}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA]">
                <td className="px-4 py-3 font-medium text-[#1C1C1E] flex items-center gap-2"><UserCog className="h-4 w-4 text-[#4B5563]" />{a.name}</td>
                <td className="px-4 py-3 text-[#4B5563]">{a.email}</td>
                <td className="px-4 py-3 text-[#4B5563] text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-ca-account-${a.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove CA access for "{a.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>This will revoke their login immediately. This action cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteAccount(a.id)} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ca-account-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">New CA Login</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="ca-account-name-input" value={form.name} onChange={set("name")} placeholder="e.g. Rajesh Sharma & Associates" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="ca-account-email-input" type="email" value={form.email} onChange={set("email")} placeholder="ca@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input data-testid="ca-account-password-input" type="password" value={form.password} onChange={set("password")} placeholder="Set a temporary password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="ca-account-cancel-button">Cancel</Button>
            <Button onClick={submit} disabled={submitting} data-testid="ca-account-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
              {submitting ? "Creating..." : "Create Login"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
